/**
 * `icon.gradient*` and `spinner.gradient*` — the brand ramp, as paint.
 *
 * Split out of `semanticTokens.ts` when the spinner stops pushed that file back
 * over the 1000-line CI ceiling, the same way `semanticTokens.badge.ts` and
 * `semanticTokens.interactive.ts` were. Being its own file suits it: these are
 * the only tokens in the theme that exist to be painted through a gradient
 * rather than applied as a flat colour, and both consumers read them through
 * `theme/iconGradient.ts` rather than by name.
 *
 * TWO RAMPS, and the difference between them is the whole reason this file is
 * worth reading.
 *
 * `icon.*` is the logo mark's ramp at the 500 step in light mode, used to fill
 * IDENTITY icons: the nav rail, and entity-type glyphs in trees, tables and
 * lists. An icon can carry it because an icon is a shape beside a LABEL, and
 * the label is what identifies the thing.
 *
 * `spinner.*` is the same three roles, its tail and mid at the 700 step in
 * light mode. A spinner is frequently the only thing on screen saying anything
 * is happening, so it has to clear WCAG 1.4.11 on its own. At the icon step, composited at the
 * opacities `BowerSpinner` draws with, the arc measured 1.71:1 at its head and
 * 2.21:1 at its mid on the light canvas, and 1.43 / 1.98 on `bg.subtle`. Every
 * point under 3:1, where what it replaced cleared it (`accent.solid` 3.27).
 *
 * The HEAD is the exception: it stays Sunset 500, the brand colour, at 1.71 /
 * 1.43 (David, 2026-10-17), recorded as shortfalls in the same test, so on the
 * tinted row no identifying stop reaches 3:1 (with the mid below). The 700
 * step's mid measures 3.21 on the page grounds and 2.90 on the tinted row.
 * That last number is 0.10 under, and it is a chosen trade rather than an
 * oversight (David, 2026-09-24): the 800 step clears every ground at 5.89 /
 * 4.04 and 4.92 / 3.68, and reads navy-to-brown instead of the brand's
 * blue-to-orange. The shortfall is recorded as data in
 * `__tests__/spinnerContrast.test.ts` so it cannot widen unnoticed, and so that
 * closing it later forces the exemption to be deleted rather than left behind.
 *
 * DARK IS SHARED, byte for byte. There was nothing to fix there (4.14 mid,
 * 11.20 head) and diverging would cost the two marks their common identity for
 * no gain.
 *
 * Measured by `__tests__/spinnerContrast.test.ts`, which composites each stop
 * at the opacity it is drawn with. `__tests__/iconGradientContrast.test.ts`
 * resolves the icon stops at full opacity, which is right for an icon and
 * structurally blind to a stop drawn at 0.18 — that blindness is what let the
 * spinner ship at 1.43:1 with a green suite.
 */

export const gradientTokens = {
  // ── Brand icon gradient ───────────────────────────────────────────
  // The three-stop ramp the logo mark is drawn in, reused as the fill for
  // IDENTITY icons: the nav rail, and entity-type glyphs in trees, tables
  // and lists. Utility/affordance icons (close, caret, paperclip-in-
  // composer, overflow dots) deliberately stay flat ink — a gradient on
  // every control reads as noise at 14px and costs legibility.
  //
  // Declared as CSS-var references rather than literals so a rebrand stays
  // the one-file edit in roles.ts that tokenHygiene.test.ts enforces. The
  // stops are the info / accent / warning roles, which is the same ramp
  // assets/brand/logo-mark.svg paints its own gradient with.
  //
  // Consumed via `iconGradient.ts`, never by naming these tokens directly:
  // a gradient is a background-image, and it needs the paired
  // background-clip + forced-colors fallback to be safe to render.
  //
  // The three stops are tokens in their own right because TWO mechanisms
  // need them and must not drift apart: the CSS gradient below (for
  // MaterialIcon, which is ligature TEXT and so can be background-clipped)
  // and the <linearGradient> defs that tint the inline-SVG BowerIcon set
  // (background-clip does nothing on SVG, so that path needs real <stop>
  // elements). One declaration, two renderers.
  //
  // DARK: every stop lifts one step. The mid stop is the darkest point of
  // the ramp and so the one that decides whether the set survives the dark
  // canvas — see __tests__/iconGradientContrast.test.ts, which measures all
  // three stops against both canvases rather than trusting this comment.
  'icon.gradientStop1': { default: 'info.500', _dark: 'info.400' },
  'icon.gradientStop2': { default: 'accent.500', _dark: 'accent.400' },
  'icon.gradientStop3': { default: 'warning.500', _dark: 'warning.400' },

  // Spinner arc. The icon ramp's roles, darker in light mode: at the icon
  // step the arc measured 1.43:1 and failed WCAG 1.4.11. Separate tokens so
  // fixing a spinner does not restyle every gradient icon. Numbers, and the
  // tail's exemption, in __tests__/spinnerContrast.test.ts.
  // The head is Sunset in both modes, because it is the brand colour (David,
  // 2026-10-17). Light uses Sunset 500, the icon ramp's own light step; it
  // does not reach 3:1 on the cream grounds, a recorded shortfall in
  // spinnerContrast.test.ts. It replaced Charcoal (2026-09-28), which passed
  // but read as a different mark from every gradient icon beside it.
  'spinner.gradientStop1': { default: 'info.700', _dark: 'info.400' },
  'spinner.gradientStop2': { default: 'accent.700', _dark: 'accent.400' },
  'spinner.gradientStop3': { default: 'warning.500', _dark: 'warning.400' },

  // No `_dark` pair on either composite, deliberately: the STOPS are already
  // mode-aware, so one declaration adapts. A byte-identical `_dark` here does
  // nothing except invite the drift this block warns about — someone edits
  // `default`'s stop order or offsets and light/dark silently diverge.
  'icon.gradient': {
    default:
      'linear-gradient(135deg, var(--chakra-colors-icon-gradientStop1) 0%, var(--chakra-colors-icon-gradientStop2) 45%, var(--chakra-colors-icon-gradientStop3) 100%)',
  },
  // Cool variant. The warm tail is what makes a magnifying glass read as a
  // lollipop (the warm blob lands exactly on the lens), so search-like
  // glyphs push the warm stop off the top-left corner at -40% and finish on
  // the accent instead. Same three tokens, re-ordered and re-positioned.
  'icon.gradientCool': {
    default:
      'linear-gradient(135deg, var(--chakra-colors-icon-gradientStop3) -40%, var(--chakra-colors-icon-gradientStop1) 30%, var(--chakra-colors-icon-gradientStop2) 100%)',
  },
};
